import React from 'react';
import PropTypes from 'prop-types';
import getClientDimensions from '../../../../../../../../../../../shared/lib/get-client-dimensions/index';
import componentBase from '../../../../../../../../../../../src/lib/component-base';
import { prefersMPHSpeedFormat, prefersKPHSpeedFormat } from '../../../../../../../../../../../src/lib/unit-measurement-format';

const knotsPer1MPH = 0.868976;
const avgTopFlightSpeedMPH = 633;
const startColorGradient = 'rgba(129, 162, 202, 0.5)';
const startColorGradientHex = '#8081A2CA';
const endColorGradient = 'rgba(0, 0, 0, 1)';
const endColorGradientHex = '#00000000';

let resizeTimeout;
let Raphael;

const resizeThrottlerFunctionGenerator = actualResizeHandler => () => {
  if (!resizeTimeout) {
    resizeTimeout = setTimeout(() => {
      resizeTimeout = null;
      actualResizeHandler();
    }, 66);
  }
};

@componentBase('FlightMiniTrackerSpeed')
export default class FlightMiniTrackerSpeed extends React.Component {
  static propTypes = {
    asKnots: PropTypes.bool,
    currentSpeedMPH: PropTypes.number,
  };

  constructor(props, context) {
    super(props, context);

    this._adjustGraphOnResize = this._adjustGraphOnResize.bind(this);

    this.muiDefaultProps = {
      topStyle: {
        padding: '0',
        height: 'inherit',
        background: `-webkit-linear-gradient(${startColorGradient}, ${endColorGradient})`, /* For Safari 5.1 to 6.0 */
        background: `-o-linear-gradient(${startColorGradient}, ${endColorGradient})`, /* For Opera 11.1 to 12.0 */
        background: `-moz-linear-gradient(${startColorGradient}, ${endColorGradient})`, /* For Firefox 3.6 to 15 */
        background: `filter: progid:DXImageTransform.Microsoft.gradient(startColorstr=${startColorGradientHex}, endColorstr=${endColorGradientHex})`, /* IE */
        background: `linear-gradient(${startColorGradient}, ${endColorGradient})`, /* Standard syntax */
      },
    };
  }

  componentDidMount() {
    Raphael = require('raphael');
    this.resizeListener = resizeThrottlerFunctionGenerator(this._adjustGraphOnResize);
    window.addEventListener('resize', this.resizeListener);
    this._adjustGraphOnResize();
  }

  componentWillReceiveProps() {
    this._adjustGraphOnResize();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resizeListener);
  }

  _adjustGraphOnResize() {
    if (this.props.currentSpeedMPH) {
      this._clearGraph();
      this._drawGraph();
    }
  }

  _clearGraph() {
    if (this.paper) {
      this.paper.clear();
    }
  }

  _drawGraph() {
    if (document.getElementById('speedGraph')) {
      const [xWidth, xHeight] = getClientDimensions(document, 'speedGraph');

      this.paper = this.paper || Raphael(this.refs.speedGraph, '100%', xHeight); // eslint-disable-line

      this.paper.customAttributes.speedArc = (centerX, centerY, percentage, radius) => {
        if (!radius) {
          return;
        }

        const radians = (Math.PI / 2) - (percentage * 2 * Math.PI);
        const arcX = centerX + (radius * Math.cos(radians));
        const arcY = centerY - (radius * Math.sin(radians));
        const path = [['M', centerX, centerY - radius],
          ['M', centerX, centerY - radius],
          ['A', radius, radius, 0, +(percentage > 0.5), 1, arcX, arcY]];

        return {
          path,
        };
      };

      const circle = this.paper.circle(xWidth / 2, xHeight / 2, xHeight / 2 - 15).attr({
        stroke: '#FFFFFF',
        'stroke-width': 10,
      });

      const centerX = circle.attrs.cx;
      const centerY = circle.attrs.cy;
      const radius = circle.attrs.r;

      const currentSpeedGraph = this.paper.path();
      currentSpeedGraph
        .attr('stroke', '#FF9600')
        .attr('stroke-width', '12');
      currentSpeedGraph.attr({
        speedArc: [centerX, centerY, this._getSpeedGraphPercentage(), radius],
      });
    }
  }

  _getSpeedInKnots(speedMPH) {
    return (speedMPH * knotsPer1MPH).toFixed(0);
  }

  _getSpeedInKPH(speedMPH) {
    return (speedMPH * 1.60934).toFixed(0);
  }

  _getSpeedGraphPercentage() {
    const { currentSpeedMPH } = this.props;
    const pct = parseInt(currentSpeedMPH, 10) / avgTopFlightSpeedMPH;
    // A format problem occurs if percentage reaches 1.0
    return Math.max(0.0, Math.min(0.995, pct));
  }

  _getSpeed() {
    const speed = { value: '--', units: 'kts' };

    if (prefersMPHSpeedFormat(this.context.currentUser)) {
      speed.units = 'mph';
      if (this.props.currentSpeedMPH) {
        speed.value = this.props.currentSpeedMPH;
      }
    } else if (prefersKPHSpeedFormat(this.context.currentUser)) {
      speed.units = 'km/h';
      if (this.props.currentSpeedMPH) {
        speed.value = this._getSpeedInKPH(this.props.currentSpeedMPH);
      }
    } else if (this.props.currentSpeedMPH) {
      speed.value = this._getSpeedInKnots(this.props.currentSpeedMPH);
    }

    return `${speed.value} ${speed.units}`;
  }

  render() {
    // console.log('[FlightMiniTrackerSpeed] render : props', this.props);

    return (
      <div style={this.muiDefaultProps.topStyle}>
        <div id='speedGraph' ref='speedGraph' style={{ position: 'absolute', height: 'inherit', width: '100%' }} />
        <div className='col-xs-12 col-sm-12 col-md-12' style={{ padding: '0', height: '50%' }}>
          <div
            className='col-xs-12 col-sm-12 col-md-12'
            style={{
 position: 'absolute', padding: '0', bottom: '0', textAlign: 'center',
}}
          >
            <span style={{ fontSize: '12pt' }}>Speed</span>
          </div>
        </div>
        <div className='col-xs-12 col-sm-12 col-md-12' style={{ padding: '0', height: '50%' }}>
          <div
            className='col-xs-12 col-sm-12 col-md-12'
            style={{
 position: 'absolute', padding: '0', top: '0', textAlign: 'center',
}}
          >
            <span style={{ fontSize: '13pt', fontWeight: 'bold', color: '#FF9600' }}>{ this._getSpeed() }</span>
          </div>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./modules/SingleFlight/children/FlightTracker/children/FlightPositionalInformationSection/children/FlightPositionalInformation/children/FlightMiniTracker/children/FlightMiniTrackerSpeed.js